/**
 * P07 B6 (DEC-115 A): báo giá đa ngôn ngữ.
 * - Ngôn ngữ in PDF/Excel: Tiếng Việt / English / Song ngữ Việt–Anh; tiếng Trung / Song ngữ Việt–Trung (anh Tiến 06/10/2026:
 *   "khách Trung nhiều"). Tiền vẫn VND.
 * - Tên dịch của sản phẩm do ERP giữ (Asia không có) ở bảng product_translations (en, zh); Nhật/Hàn thêm sau bằng cách
 *   mở rộng PRODUCT_TRANSLATION_LANGS + migration mở CHECK.
 */
import { autoTranslateName } from './quote-glossary';
export const QUOTE_LANGUAGES = ['vi', 'en', 'vi_en', 'zh', 'vi_zh'] as const;
export type QuoteLanguage = (typeof QUOTE_LANGUAGES)[number];

export const QUOTE_LANGUAGE_LABELS: Record<QuoteLanguage, string> = {
  vi: 'Tiếng Việt',
  en: 'English',
  vi_en: 'Song ngữ Việt – Anh',
  zh: 'Tiếng Trung (中文)',
  vi_zh: 'Song ngữ Việt – Trung',
};

/** Ngôn ngữ có tên dịch của sản phẩm. */
export const PRODUCT_TRANSLATION_LANGS = ['en', 'zh'] as const;
export type ProductTranslationLang = (typeof PRODUCT_TRANSLATION_LANGS)[number];

export const TRANSLATION_LANG_LABELS: Record<ProductTranslationLang, string> = { en: 'tiếng Anh', zh: 'tiếng Trung' };

/** Ngôn ngữ dịch mà bản in dùng (null = chỉ tiếng Việt). */
export function foreignLangOf(lang: QuoteLanguage): ProductTranslationLang | null {
  return lang === 'vi' ? null : lang === 'en' || lang === 'vi_en' ? 'en' : 'zh';
}

/** Bản in song ngữ (tiếng Việt + 1 ngôn ngữ dịch). */
export const isBilingual = (lang: QuoteLanguage) => lang === 'vi_en' || lang === 'vi_zh';

/** Bản in có dùng tên tiếng Anh của dòng hàng. */
export const usesEnglish = (lang: QuoteLanguage) => foreignLangOf(lang) === 'en';

/** Dòng (số thứ tự) thiếu tên tiếng Anh: bản en / vi_en sẽ in tên tiếng Việt cho các dòng này → cảnh báo trước khi xuất. */
export function missingEnglishLines(items: readonly { lineNo: number; nameEn?: string | null }[]): number[] {
  return items.filter((i) => !i.nameEn?.trim()).map((i) => i.lineNo);
}

/** Dòng thiếu tên theo ngôn ngữ dịch của bản in (rỗng khi bản in chỉ tiếng Việt). */
export function missingTranslationLines(
  lang: QuoteLanguage,
  items: readonly { lineNo: number; nameEn?: string | null; nameZh?: string | null; productName?: string | null }[],
): number[] {
  const f = foreignLangOf(lang);
  if (!f) return [];
  // Tên dịch được trọn theo bảng thuật ngữ (quote-glossary) không tính là thiếu
  return items.filter((i) => !(f === 'en' ? i.nameEn : i.nameZh)?.trim() && !autoTranslateName(i.productName, f)).map((i) => i.lineNo);
}

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const SCALES = ['', ' thousand', ' million', ' billion', ' trillion', ' quadrillion'];

function readHundreds(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  const parts: string[] = [];
  if (h) parts.push(`${ONES[h]} hundred`);
  if (r >= 20) parts.push(r % 10 ? `${TENS[Math.floor(r / 10)]}-${ONES[r % 10]}` : TENS[Math.floor(r / 10)]!);
  else if (r > 0) parts.push(ONES[r]!);
  return parts.join(' ');
}

/** Đọc số tiền VND bằng chữ tiếng Anh (số nguyên không âm), VD 1.250.000 → "One million two hundred fifty thousand Vietnam dong". */
export function moneyToEnglishWords(amount: number): string {
  const n = Math.round(Math.abs(amount));
  if (n === 0) return 'Zero Vietnam dong';
  const groups: number[] = [];
  let rest = n;
  while (rest > 0) {
    groups.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }
  const words: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) if (groups[i]) words.push(readHundreds(groups[i]!) + SCALES[i]);
  const s = words.join(' ');
  return `${s.charAt(0).toUpperCase()}${s.slice(1)} Vietnam dong`;
}

// Chữ số viết hoa tài chính (大写) dùng trên chứng từ thương mại Trung Quốc: không sửa được bằng một nét.
const ZH_DIGITS = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
const ZH_UNITS = ['', '拾', '佰', '仟'];
const ZH_SECTIONS = ['', '万', '亿', '万亿'];

/** Đọc 1 nhóm 4 chữ số (0–9999); `lead` = có nhóm lớn hơn đứng trước (cần "零" khi nhóm < 1000). */
function readZhSection(n: number): string {
  let out = '';
  let zero = false;
  for (let u = 3; u >= 0; u--) {
    const d = Math.floor(n / 10 ** u) % 10;
    if (d === 0) {
      if (out) zero = true;
      continue;
    }
    if (zero) out += ZH_DIGITS[0];
    zero = false;
    out += ZH_DIGITS[d]! + ZH_UNITS[u]!;
  }
  return out;
}

/**
 * Đọc số tiền VND bằng chữ tiếng Trung (大写, số nguyên không âm),
 * VD 1.250.000 → "壹佰贰拾伍万越南盾整".
 */
export function moneyToChineseWords(amount: number): string {
  const n = Math.round(Math.abs(amount));
  if (n === 0) return '零越南盾整';
  const sections: number[] = [];
  let rest = n;
  while (rest > 0) {
    sections.push(rest % 10_000);
    rest = Math.floor(rest / 10_000);
  }
  let out = '';
  let pendingZero = false;
  for (let i = sections.length - 1; i >= 0; i--) {
    const sec = sections[i]!;
    if (sec === 0) {
      if (out) pendingZero = true;
      continue;
    }
    // Nhóm sau (nhỏ hơn) không đủ 4 chữ số, hoặc có nhóm 0 xen giữa → đọc "零"
    if (out && (pendingZero || sec < 1000)) out += ZH_DIGITS[0];
    pendingZero = false;
    out += readZhSection(sec) + ZH_SECTIONS[i]!;
  }
  return `${out}越南盾整`;
}
